import { useState, useCallback, memo, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCartOutlined } from '@mui/icons-material';
import type { GetViewByTypeRes } from 'types';
import { CartIconButton, EmptyCartMessage, StyledBadge, StyledMenu } from './ShoppingCart.styles';
import { CartItem } from './CartItem';
import { useShoppingCartData } from 'utils/hooks/useShoppingCartData';

interface ShoppingCartProps {
  shoppingCarts: GetViewByTypeRes['views'];
}

export const ShoppingCart = memo(({ shoppingCarts }: ShoppingCartProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { id: currentModuleId, agendaId: currentAgendaId, recordId } = useParams<{ id: string; agendaId: string; recordId: string }>();
  const { t } = useTranslation();

  const { carts, totalItemCount } = useShoppingCartData({
    shoppingCarts,
    currentModuleId,
    currentAgendaId,
    recordId,
  });

  const handleCartClick = useCallback(
    (viewId: string, agendaId: string, moduleId: string) => {
      setAnchorEl(null);
      navigate(`/user/modules/${moduleId}/Flow/${agendaId}/view/${viewId}`);
    },
    [navigate]
  );

  return (
    <Fragment>
      <StyledBadge badgeContent={totalItemCount}>
        <CartIconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <ShoppingCartOutlined sx={{ color: '#4F46E5', fontSize: 24 }} />
        </CartIconButton>
      </StyledBadge>
      <StyledMenu
        anchorEl={anchorEl}
        disableScrollLock
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        elevation={0}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}>
        {carts.length > 0 ? (
          carts.map((cart) => <CartItem key={cart.id} {...cart} onClick={handleCartClick} />)
        ) : (
          <EmptyCartMessage>{t('flow.view.emptyCart')}</EmptyCartMessage>
        )}
      </StyledMenu>
    </Fragment>
  );
});
